import { TanStackDevtools } from '@tanstack/react-devtools'
import type { QueryClient } from '@tanstack/react-query'
import {
  createRootRouteWithContext,
  HeadContent,
  Scripts,
  useRouterState,
} from '@tanstack/react-router'
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'
import { useMemo } from 'react'
import { CookieNotice } from '../components/CookieNotice'
import Footer from '../components/Footer'
import Header from '../components/Header'
import { TrialBanner } from '../components/marketing/TrialBanner'
import { Toaster } from '../components/ui/sonner'
import { TooltipProvider } from '../components/ui/tooltip'
import { AnalyticsIdentify } from '../lib/analytics/AnalyticsIdentify'
import { readConsent, shouldStartOptedOut } from '../lib/analytics/consent'
import { consentMode } from '../lib/analytics/consent-mode'
import {
  POSTHOG_KEY,
  PostHogProvider,
  posthogOptions,
} from '../lib/analytics/posthog'
import { serverEnabledGatewayIds } from '../lib/billing/gateways/availability'
import { serverPublicTrialOffer } from '../lib/billing/trial-offer-runtime'
import {
  DEFAULT_DESCRIPTION,
  OG_IMAGE_ALT,
  OG_IMAGE_PATH,
  SITE_NAME,
  SITE_URL,
} from '../lib/seo'

import appCss from '../styles.css?url'

const THEME_INIT_SCRIPT = `(function(){try{var t=localStorage.getItem('theme')||'light';var d=t==='dark'||(t==='system'&&window.matchMedia('(prefers-color-scheme: dark)').matches);document.documentElement.classList.toggle('dark',d);}catch(e){}})();`

// Deployment env is a RUNTIME value: process.env.DEPLOYMENT_ENV differs between
// staging and prod, but the SAME promoted image serves both, so it can't be a
// build-time VITE_ constant. Read it server-side and inline it here so the client
// (PostHog's before_send, see analytics/posthog.ts) can tag every event with the
// right `environment`. `import.meta.env.SSR` compiles the server-only branch OUT of
// the client bundle; on the client we read back what the server already set, so the
// script string matches on hydration.
const DEPLOYMENT_ENV_VALUE = import.meta.env.SSR
  ? (process.env.DEPLOYMENT_ENV ?? '')
  : (window.__DEPLOYMENT_ENV__ ?? '')
// Which payment gateways are live — RUNTIME flags like DEPLOYMENT_ENV, so
// client purchase CTAs can swap to "contact sales" without a rebuild. Read
// server-side and inlined; the client reads the list back via
// billing/gateways/availability.ts. A LIST rather than one boolean so turning
// one rail off never hides another rail's CTA.
const BILLING_GATEWAYS_VALUE = import.meta.env.SSR
  ? serverEnabledGatewayIds()
  : (window.__BILLING_GATEWAYS__ ?? [])
// The trial offer, for the banner above the public header. Runtime like the
// two above so closing the offer needs no rebuild.
const TRIAL_OFFER_VALUE = import.meta.env.SSR
  ? serverPublicTrialOffer()
  : (window.__TRIAL_OFFER__ ?? null)
// The consent mode is the one runtime value that varies per REQUEST rather than
// per deploy (it comes from the visitor's `cf-ipcountry` header), so unlike the
// two above it can't be a module constant — `consentMode()` is called during
// render. It has to reach the client through this script rather than a fetch:
// posthog.init() needs it before it captures anything.
function runtimeEnvScript(consent: string): string {
  return (
    `window.__DEPLOYMENT_ENV__=${JSON.stringify(DEPLOYMENT_ENV_VALUE)};` +
    `window.__BILLING_GATEWAYS__=${JSON.stringify(BILLING_GATEWAYS_VALUE)};` +
    `window.__TRIAL_OFFER__=${JSON.stringify(TRIAL_OFFER_VALUE)};` +
    `window.__ANALYTICS_CONSENT_MODE__=${JSON.stringify(consent)};`
  )
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
  {
    head: () => ({
      meta: [
        {
          charSet: 'utf-8',
        },
        {
          name: 'viewport',
          content: 'width=device-width, initial-scale=1',
        },
        {
          title: SITE_NAME,
        },
        {
          name: 'description',
          content: DEFAULT_DESCRIPTION,
        },
        {
          name: 'theme-color',
          content: '#00aeef',
        },
        // Site-wide OG/Twitter defaults. Routes override `og:title` /
        // `og:description` / `og:url` / `og:type` (and the plain `title` /
        // `description` tags) via `pageMeta()` in `src/lib/seo.ts` — the
        // router dedupes meta by `name`/`property`, child (route) wins.
        {
          property: 'og:site_name',
          content: SITE_NAME,
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          property: 'og:title',
          content: SITE_NAME,
        },
        {
          property: 'og:description',
          content: DEFAULT_DESCRIPTION,
        },
        {
          property: 'og:url',
          content: SITE_URL,
        },
        {
          property: 'og:image',
          content: `${SITE_URL}${OG_IMAGE_PATH}`,
        },
        {
          property: 'og:image:width',
          content: '1200',
        },
        {
          property: 'og:image:height',
          content: '630',
        },
        {
          property: 'og:image:alt',
          content: OG_IMAGE_ALT,
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:image',
          content: `${SITE_URL}${OG_IMAGE_PATH}`,
        },
        {
          name: 'twitter:image:alt',
          content: OG_IMAGE_ALT,
        },
      ],
      links: [
        {
          rel: 'stylesheet',
          href: appCss,
        },
        {
          rel: 'icon',
          type: 'image/svg+xml',
          href: '/favicon.svg',
        },
        {
          rel: 'icon',
          type: 'image/png',
          sizes: '32x32',
          href: '/favicon-32.png',
        },
        {
          rel: 'icon',
          type: 'image/png',
          sizes: '16x16',
          href: '/favicon-16.png',
        },
      ],
    }),
    shellComponent: RootDocument,
  },
)

function RootDocument({ children }: { children: React.ReactNode }) {
  // Server: resolved from request headers. Client: read back from the global
  // the script below already set, so the markup matches on hydration.
  const runtimeScript = runtimeEnvScript(consentMode())
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        {/* biome-ignore lint/security/noDangerouslySetInnerHtml: set runtime deployment env + consent mode before hydration and PostHog init */}
        <script dangerouslySetInnerHTML={{ __html: runtimeScript }} />
        {/* biome-ignore lint/security/noDangerouslySetInnerHtml: inline no-FOUC theme script must run before hydration */}
        <script dangerouslySetInnerHTML={{ __html: THEME_INIT_SCRIPT }} />
        <HeadContent />
      </head>
      {/* suppressHydrationWarning: browser extensions (password managers,
          Grammarly, dark-mode, VS Code's vsc-initialized, etc.) inject classes
          and attributes onto <body> before React hydrates. */}
      <body className="antialiased" suppressHydrationWarning>
        <AnalyticsProvider>
          <TooltipProvider>
            <AppFrame>{children}</AppFrame>
            <Toaster />
          </TooltipProvider>
        </AnalyticsProvider>
        <TanStackDevtools
          config={{
            position: 'bottom-right',
          }}
          plugins={[
            {
              name: 'Tanstack Router',
              render: <TanStackRouterDevtoolsPanel />,
            },
          ]}
        />
        <Scripts />
      </body>
    </html>
  )
}

/**
 * Wraps the app with `PostHogProvider`, which initializes PostHog client-side
 * from `apiKey` + `options` (the init is a no-op during SSR). When no key is
 * configured, skip the provider entirely so it never warns about a missing key.
 */
function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  // Computed once: PostHogProvider re-runs posthog.init() whenever the options
  // object identity changes. `shouldStartOptedOut` combines the region's
  // default with any choice the visitor stored on an earlier visit, so an
  // EEA/UK visitor who has not accepted starts with capturing off.
  const options = useMemo(
    () => posthogOptions(shouldStartOptedOut(consentMode(), readConsent())),
    [],
  )

  if (!POSTHOG_KEY) return <>{children}</>

  return (
    <PostHogProvider apiKey={POSTHOG_KEY} options={options}>
      <AnalyticsIdentify />
      <CookieNotice />
      {children}
    </PostHogProvider>
  )
}

/**
 * Picks the chrome for the current route. App routes (anything under the
 * `_authenticated` layout) render bare — that layout ships its own sidebar
 * shell, full height, no marketing Footer. Everything else (home, blog,
 * marketing, auth) gets the public Header/Footer column. The choice is driven
 * by *where you are*, not auth state, so a logged-in visitor on /blog still
 * sees the marketing chrome. Router state is available on the server, so this
 * is deterministic across SSR and hydration.
 */
function AppFrame({ children }: { children: React.ReactNode }) {
  const inApp = useRouterState({
    select: (s) => s.matches.some((m) => m.routeId === '/_authenticated'),
  })

  if (inApp) return <>{children}</>

  return (
    <div className="flex min-h-svh flex-col">
      <TrialBanner />
      <Header />
      <div className="flex-1">{children}</div>
      <Footer />
    </div>
  )
}
